import { type GlassesDraftListItem, type Product } from "types"

export type GlassesRowStatus = "ready" | "need-setup" | "processing"

export type UnifiedGlassesItem =
  | {
      kind: "draft"
      id: string
      originalIndex: number
      draft: GlassesDraftListItem
      status: Exclude<GlassesRowStatus, "ready">
    }
  | {
      kind: "product"
      id: string
      originalIndex: number
      product: Product
      status: GlassesRowStatus
    }

const getGlassesEffectUrl = (product: Product): string =>
  product.effectUrl?.trim() || product.skus?.[0]?.vto?.effectUrl?.trim() || ""

const getGlassesExternalId = (product: Product): string =>
  product.externalId?.trim() || product.skus?.[0]?.sku?.trim() || ""

export const isProductComplete = (product: Product): boolean =>
  Boolean(
    product.id &&
      product.name?.trim() &&
      getGlassesExternalId(product) &&
      product.cover?.trim() &&
      getGlassesEffectUrl(product)
  )

const STATUS_ORDER: Record<GlassesRowStatus, number> = {
  ready: 0,
  "need-setup": 1,
  processing: 2,
}

const parseTimestamp = (value: string | undefined): number => {
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

const compareDrafts = (
  status: Exclude<GlassesRowStatus, "ready">,
  left: GlassesDraftListItem,
  right: GlassesDraftListItem
): number => {
  if (status === "processing") {
    const byCreatedAt = parseTimestamp(right.createdAt) - parseTimestamp(left.createdAt)
    if (byCreatedAt !== 0) return byCreatedAt
  }

  const byUpdatedAt = parseTimestamp(right.updatedAt) - parseTimestamp(left.updatedAt)
  if (byUpdatedAt !== 0) return byUpdatedAt

  return left.draftId.localeCompare(right.draftId)
}

export const buildUnifiedGlassesList = (
  products: Product[],
  drafts: GlassesDraftListItem[]
): UnifiedGlassesItem[] => {
  const draftItems: UnifiedGlassesItem[] = drafts.map((draft, originalIndex) => {
    const digitizationStatus = draft.digitization?.status
    const status: Exclude<GlassesRowStatus, "ready"> =
      digitizationStatus === "queued" || digitizationStatus === "processing"
        ? "processing"
        : "need-setup"
    return {
      kind: "draft",
      id: `draft:${draft.draftId}`,
      originalIndex,
      draft,
      status,
    }
  })

  const productItems: UnifiedGlassesItem[] = products.map((product, originalIndex) => {
    const digitizationStatus = product.digitization?.status
    let status: GlassesRowStatus
    if (digitizationStatus === "queued" || digitizationStatus === "processing")
      status = "processing"
    else if (!digitizationStatus || digitizationStatus === "ready")
      status = isProductComplete(product) ? "ready" : "need-setup"
    else status = "need-setup"

    return {
      kind: "product",
      id: `product:${product.id}`,
      originalIndex,
      product,
      status,
    }
  })

  return [...draftItems, ...productItems].sort((a, b) => {
    const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    if (byStatus !== 0) return byStatus

    if (a.kind === "draft" && b.kind === "draft") return compareDrafts(a.status, a.draft, b.draft)
    if (a.kind === "product" && b.kind === "product") return a.originalIndex - b.originalIndex

    return a.kind === "draft" ? -1 : 1
  })
}
